import { trpc } from './trpc';

/**
 * Auth state - set by ClerkTokenProvider in App.tsx
 */
let isUserAuthenticated = false;

export const setAuthenticated = (value: boolean): void => {
  isUserAuthenticated = value;
};

export const getIsAuthenticated = (): boolean => {
  return isUserAuthenticated;
};

/**
 * Admin API client wrapping tRPC calls
 * Note: Mock data in this file is only used as placeholders during loading states,
 * not as fallbacks. All functions call the live API directly.
 */
export const adminApi = {
  /**
   * Get dashboard statistics
   */
  getDashboardStats: async (period: '7d' | '30d' | '90d' = '30d') => {
    const stats = await trpc.dashboard.getDashboardStats.query({ period });
    // API returns core stats; change metrics computed client-side or defaulted
    return {
      mrr: stats.mrr,
      mrrChange: (stats as Record<string, unknown>).mrrChange as number ?? 0,
      activeUsers: stats.activeUsers,
      activeUsersChange: (stats as Record<string, unknown>).activeUsersChange as number ?? 0,
      creditsConsumed: stats.creditsConsumed,
      creditsConsumedChange: (stats as Record<string, unknown>).creditsConsumedChange as number ?? 0,
      churnRate: (stats as Record<string, unknown>).churnRate as number ?? 0,
      churnRateChange: (stats as Record<string, unknown>).churnRateChange as number ?? 0,
    };
  },

  /**
   * Get churn alerts
   */
  getChurnAlerts: async (limit: number = 10) => {
    const data = await trpc.dashboard.getChurnAlerts.query({ limit });
    return data.alerts.map((alert: { id: string; userId: string; email: string; riskScore: number; reason: string; createdAt: string }) => ({
      id: alert.id,
      userId: alert.userId,
      email: alert.email,
      riskScore: alert.riskScore,
      reason: alert.reason,
      createdAt: alert.createdAt,
    }));
  },

  /**
   * Get mode usage statistics
   */
  getModeUsage: async (days: number = 30) => {
    const data = await trpc.dashboard.getModeUsage.query({ days });
    return data.usage.map((item: { mode: 'collaborative' | 'adversarial'; sessions: number; avgDuration: number }) => ({
      mode: item.mode,
      sessions: item.sessions,
      avgDuration: item.avgDuration,
    }));
  },

  /**
   * Get DAP (Digital Adoption Platform) scores
   */
  getDAPScores: async () => {
    const data = await trpc.dashboard.getDAPScores.query();
    return {
      average: data.average,
      distribution: data.distribution.map((item: { score: string; count: number }) => ({
        score: item.score,
        count: item.count,
      })),
    };
  },

  /**
   * Get revenue data over time
   */
  getRevenueData: async (days: number = 30) => {
    const data = await trpc.dashboard.getRevenueData.query({ days });
    return data.data;
  },

  /**
   * Get recent signups
   */
  getRecentSignups: async (limit: number = 5) => {
    const data = await trpc.dashboard.getRecentSignups.query({ limit });
    return data.signups.map((signup: { userId: string; tier: string; createdAt: number }) => ({
      id: signup.userId,
      email: signup.userId, // Email not exposed in API for privacy
      tier: signup.tier,
      createdAt: new Date(signup.createdAt * 1000).toISOString(),
    }));
  },

  /**
   * Get users list with pagination and filtering
   */
  getUsers: async (options: {
    limit?: number;
    offset?: number;
    tier?: 'free' | 'pro' | 'team' | 'enterprise';
    status?: 'active' | 'inactive' | 'suspended';
  } = {}): Promise<{ users: User[]; total: number }> => {
    const data = await trpc.dashboard.getUsers.query(options);
    return {
      users: data.users.map((user): User => {
        const lastActive = user.lastActiveAt;
        return {
          id: user.userId,
          email: user.userId, // Email not exposed in API for privacy
          tier: user.tier as 'free' | 'pro' | 'enterprise',
          credits: user.monthlyAllocation,
          creditsUsed: user.monthlyAllocation - user.balance,
          status: user.status as 'active' | 'inactive' | 'suspended',
          createdAt: new Date(user.createdAt * 1000).toISOString(),
          lastActiveAt: lastActive
            ? new Date(typeof lastActive === 'number' ? lastActive * 1000 : lastActive).toISOString()
            : new Date(user.createdAt * 1000).toISOString(),
          churnRisk: 'low', // Would need ML model for actual risk
        };
      }),
      total: data.total,
    };
  },

  /**
   * Get analytics data
   */
  getAnalytics: async (days: number = 30) => {
    const data = await trpc.dashboard.getAnalytics.query({ days });
    return {
      modelUsage: data.modelUsage.map((m: { model: string; requests: number; credits: number; percentage: number }) => ({
        model: m.model,
        requests: m.requests,
        credits: m.credits,
        percentage: m.percentage,
      })),
      creditsOverTime: data.dailyStats.map((stat: { date: string; credits: number }) => ({
        date: stat.date,
        credits: stat.credits,
      })),
    };
  },

  /**
   * Get admin user info
   */
  getAdminMe: async () => {
    return trpc.admin.me.query();
  },

  /**
   * List admin users
   */
  listAdmins: async (options: { includeRevoked?: boolean; limit?: number; offset?: number } = {}) => {
    return trpc.admin.listAdmins.query(options);
  },
};

// API Types
export interface User {
  id: string;
  email: string;
  tier: 'free' | 'pro' | 'enterprise';
  credits: number;
  creditsUsed: number;
  status: 'active' | 'inactive' | 'suspended';
  createdAt: string;
  lastActiveAt: string;
  churnRisk: 'low' | 'medium' | 'high';
}

export interface DashboardMetrics {
  mrr: number;
  mrrChange: number;
  activeUsers: number;
  activeUsersChange: number;
  creditsConsumed: number;
  creditsConsumedChange: number;
  churnRate: number;
  churnRateChange: number;
}

export interface RevenueDataPoint {
  date: string;
  revenue: number;
  subscriptions: number;
}

export interface Signup {
  id: string;
  email: string;
  tier: string;
  createdAt: string;
}

export interface ChurnAlert {
  id: string;
  userId: string;
  email: string;
  riskScore: number;
  reason: string;
  createdAt: string;
}

export interface SupportTicket {
  id: string;
  userId: string;
  email: string;
  subject: string;
  status: 'open' | 'in_progress' | 'resolved';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  messages: TicketMessage[];
  createdAt: string;
  updatedAt: string;
}

export interface TicketMessage {
  id: string;
  sender: 'user' | 'agent' | 'ai';
  content: string;
  timestamp: string;
}

export interface ModelUsage {
  model: string;
  requests: number;
  credits: number;
  percentage: number;
}

export interface CreditsOverTime {
  date: string;
  credits: number;
}

export interface ModeUsage {
  mode: 'collaborative' | 'adversarial';
  sessions: number;
  avgDuration: number;
}

export interface ABTest {
  id: string;
  name: string;
  status: 'draft' | 'running' | 'paused' | 'completed';
  variants: ABVariant[];
  startDate: string | null;
  endDate: string | null;
  targetMetric: string;
}

export interface ABVariant {
  id: string;
  name: string;
  traffic: number;
  conversions: number;
  conversionRate: number;
}

export interface PriceAdjustment {
  id: string;
  tier: string;
  currentPrice: number;
  suggestedPrice: number;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
}

export interface ChurnTrigger {
  id: string;
  name: string;
  condition: string;
  action: string;
  enabled: boolean;
}

export interface ModelRoutingRule {
  id: string;
  name: string;
  condition: string;
  targetModel: string;
  priority: number;
  enabled: boolean;
}

// Mock data generators for development
export const mockData = {
  dashboardMetrics: (): DashboardMetrics => ({
    mrr: 127450,
    mrrChange: 12.5,
    activeUsers: 3847,
    activeUsersChange: 8.2,
    creditsConsumed: 2847562,
    creditsConsumedChange: 15.3,
    churnRate: 2.1,
    churnRateChange: -0.4,
  }),

  revenueData: (): RevenueDataPoint[] => {
    const data: RevenueDataPoint[] = [];
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      data.push({
        date: date.toISOString().split('T')[0] ?? '',
        revenue: 3500 + Math.random() * 2000,
        subscriptions: 80 + Math.floor(Math.random() * 40),
      });
    }
    return data;
  },

  recentSignups: (): Signup[] => [
    { id: '1', email: 'alice@startup.io', tier: 'pro', createdAt: '2024-01-05T14:30:00Z' },
    { id: '2', email: 'bob@enterprise.com', tier: 'enterprise', createdAt: '2024-01-05T13:15:00Z' },
    { id: '3', email: 'carol@dev.tech', tier: 'free', createdAt: '2024-01-05T12:00:00Z' },
    { id: '4', email: 'dave@agency.co', tier: 'pro', createdAt: '2024-01-05T10:45:00Z' },
    { id: '5', email: 'eve@corp.net', tier: 'pro', createdAt: '2024-01-05T09:30:00Z' },
  ],

  churnAlerts: (): ChurnAlert[] => [
    { id: '1', userId: 'u1', email: 'at-risk@company.com', riskScore: 0.89, reason: 'No login in 14 days, credit usage dropped 80%', createdAt: '2024-01-05T08:00:00Z' },
    { id: '2', userId: 'u2', email: 'declining@startup.io', riskScore: 0.76, reason: 'Cancelled scheduled tasks, downgraded tier inquiry', createdAt: '2024-01-04T16:00:00Z' },
    { id: '3', userId: 'u3', email: 'quiet@agency.co', riskScore: 0.71, reason: 'API calls decreased 60% week-over-week', createdAt: '2024-01-04T12:00:00Z' },
  ],

  users: (): User[] => [
    { id: '1', email: 'john@techcorp.com', tier: 'enterprise', credits: 100000, creditsUsed: 45230, status: 'active', createdAt: '2023-06-15T10:00:00Z', lastActiveAt: '2024-01-05T14:30:00Z', churnRisk: 'low' },
    { id: '2', email: 'sarah@startup.io', tier: 'pro', credits: 10000, creditsUsed: 8750, status: 'active', createdAt: '2023-09-22T08:00:00Z', lastActiveAt: '2024-01-05T12:15:00Z', churnRisk: 'low' },
    { id: '3', email: 'mike@freelance.dev', tier: 'free', credits: 1000, creditsUsed: 950, status: 'active', createdAt: '2023-11-10T14:00:00Z', lastActiveAt: '2024-01-04T18:00:00Z', churnRisk: 'medium' },
    { id: '4', email: 'lisa@agency.co', tier: 'pro', credits: 10000, creditsUsed: 2100, status: 'active', createdAt: '2023-12-01T09:00:00Z', lastActiveAt: '2024-01-05T10:00:00Z', churnRisk: 'low' },
    { id: '5', email: 'alex@bigco.net', tier: 'enterprise', credits: 100000, creditsUsed: 78500, status: 'active', createdAt: '2023-04-20T11:00:00Z', lastActiveAt: '2024-01-05T16:45:00Z', churnRisk: 'low' },
    { id: '6', email: 'emma@design.studio', tier: 'pro', credits: 10000, creditsUsed: 1200, status: 'inactive', createdAt: '2023-10-05T13:00:00Z', lastActiveAt: '2023-12-20T09:00:00Z', churnRisk: 'high' },
    { id: '7', email: 'david@consulting.biz', tier: 'pro', credits: 10000, creditsUsed: 6800, status: 'active', createdAt: '2023-08-15T10:00:00Z', lastActiveAt: '2024-01-05T11:30:00Z', churnRisk: 'low' },
    { id: '8', email: 'nina@research.edu', tier: 'free', credits: 1000, creditsUsed: 1000, status: 'active', createdAt: '2023-12-10T16:00:00Z', lastActiveAt: '2024-01-03T14:00:00Z', churnRisk: 'medium' },
    { id: '9', email: 'chris@mobile.app', tier: 'pro', credits: 10000, creditsUsed: 4500, status: 'suspended', createdAt: '2023-07-25T08:00:00Z', lastActiveAt: '2023-12-15T10:00:00Z', churnRisk: 'high' },
    { id: '10', email: 'kate@ecommerce.shop', tier: 'enterprise', credits: 100000, creditsUsed: 32000, status: 'active', createdAt: '2023-05-30T12:00:00Z', lastActiveAt: '2024-01-05T15:00:00Z', churnRisk: 'low' },
  ],

  modelUsage: (): ModelUsage[] => [
    { model: 'Claude 3 Opus', requests: 145230, credits: 1452300, percentage: 35 },
    { model: 'Claude 3 Sonnet', requests: 287450, credits: 862350, percentage: 42 },
    { model: 'Claude 3 Haiku', requests: 512000, credits: 256000, percentage: 18 },
    { model: 'Claude 2.1', requests: 34500, credits: 103500, percentage: 5 },
  ],

  creditsOverTime: (): CreditsOverTime[] => {
    const data: CreditsOverTime[] = [];
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      data.push({
        date: date.toISOString().split('T')[0] ?? '',
        credits: 80000 + Math.floor(Math.random() * 40000),
      });
    }
    return data;
  },

  modeUsage: (): ModeUsage[] => [
    { mode: 'collaborative', sessions: 28450, avgDuration: 12.5 },
    { mode: 'adversarial', sessions: 8230, avgDuration: 8.2 },
  ],

  dapScores: () => ({
    average: 7.8,
    distribution: [
      { score: '1-2', count: 45 },
      { score: '3-4', count: 120 },
      { score: '5-6', count: 580 },
      { score: '7-8', count: 1850 },
      { score: '9-10', count: 1205 },
    ],
  }),

  supportTickets: (): SupportTicket[] => [
    {
      id: 't1',
      userId: 'u1',
      email: 'frustrated@company.com',
      subject: 'API rate limiting issues',
      status: 'open',
      priority: 'high',
      messages: [
        { id: 'm1', sender: 'user', content: 'I keep hitting rate limits even though I should have 10k credits. This is blocking my production workflow.', timestamp: '2024-01-05T10:00:00Z' },
        { id: 'm2', sender: 'ai', content: 'I understand you\'re experiencing rate limiting issues. I\'ve checked your account and see you have 10,000 credits remaining. The rate limit is separate from credits - it\'s 100 requests per minute. Could you share your request pattern?', timestamp: '2024-01-05T10:01:00Z' },
        { id: 'm3', sender: 'user', content: 'I\'m making batch requests for document processing. Sometimes 200+ in a minute. Can this be increased?', timestamp: '2024-01-05T10:05:00Z' },
        { id: 'm4', sender: 'ai', content: 'I\'ve escalated this to our support team as rate limit increases require manual review. A human agent will follow up shortly.', timestamp: '2024-01-05T10:06:00Z' },
      ],
      createdAt: '2024-01-05T10:00:00Z',
      updatedAt: '2024-01-05T10:06:00Z',
    },
    {
      id: 't2',
      userId: 'u2',
      email: 'billing@startup.io',
      subject: 'Duplicate charge on invoice',
      status: 'in_progress',
      priority: 'medium',
      messages: [
        { id: 'm1', sender: 'user', content: 'I was charged twice for my Pro subscription this month. Invoice #INV-2024-0892 and #INV-2024-0893.', timestamp: '2024-01-04T15:00:00Z' },
        { id: 'm2', sender: 'ai', content: 'I apologize for the inconvenience. I can see both charges in our system. This appears to be a duplicate charge that occurred during a payment gateway timeout. I\'ve flagged this for immediate refund processing.', timestamp: '2024-01-04T15:02:00Z' },
        { id: 'm3', sender: 'agent', content: 'Hi, I\'ve processed the refund for invoice #INV-2024-0893. You should see it reflected in 3-5 business days. I\'ve also added 500 bonus credits to your account for the inconvenience.', timestamp: '2024-01-04T16:30:00Z' },
      ],
      createdAt: '2024-01-04T15:00:00Z',
      updatedAt: '2024-01-04T16:30:00Z',
    },
    {
      id: 't3',
      userId: 'u3',
      email: 'dev@agency.co',
      subject: 'Model response quality degradation',
      status: 'open',
      priority: 'urgent',
      messages: [
        { id: 'm1', sender: 'user', content: 'Since yesterday, Claude responses seem shorter and less detailed than before. We rely on this for client deliverables. Is there a known issue?', timestamp: '2024-01-05T09:00:00Z' },
        { id: 'm2', sender: 'ai', content: 'Thank you for reporting this. I don\'t see any system-wide changes, but I\'d like to understand better. Could you share: 1) Which model you\'re using, 2) Example prompts that worked before but don\'t now, 3) Any changes to your system prompt?', timestamp: '2024-01-05T09:02:00Z' },
      ],
      createdAt: '2024-01-05T09:00:00Z',
      updatedAt: '2024-01-05T09:02:00Z',
    },
    {
      id: 't4',
      userId: 'u4',
      email: 'enterprise@bigcorp.net',
      subject: 'SSO integration not working',
      status: 'resolved',
      priority: 'high',
      messages: [
        { id: 'm1', sender: 'user', content: 'Our Okta SSO integration stopped working after your update on Friday.', timestamp: '2024-01-03T11:00:00Z' },
        { id: 'm2', sender: 'agent', content: 'We identified a regression in our SAML parser. A fix has been deployed. Could you try again?', timestamp: '2024-01-03T14:00:00Z' },
        { id: 'm3', sender: 'user', content: 'Confirmed working now. Thank you for the quick resolution!', timestamp: '2024-01-03T14:30:00Z' },
      ],
      createdAt: '2024-01-03T11:00:00Z',
      updatedAt: '2024-01-03T14:30:00Z',
    },
  ],

  abTests: (): ABTest[] => [
    {
      id: 'ab1',
      name: 'Onboarding Flow v2',
      status: 'running',
      variants: [
        { id: 'v1', name: 'Control', traffic: 50, conversions: 234, conversionRate: 12.3 },
        { id: 'v2', name: 'Simplified Steps', traffic: 50, conversions: 287, conversionRate: 15.1 },
      ],
      startDate: '2024-01-01T00:00:00Z',
      endDate: null,
      targetMetric: 'trial_to_paid_conversion',
    },
    {
      id: 'ab2',
      name: 'Pricing Page Layout',
      status: 'running',
      variants: [
        { id: 'v1', name: 'Current', traffic: 33, conversions: 145, conversionRate: 8.7 },
        { id: 'v2', name: 'Feature Comparison', traffic: 33, conversions: 178, conversionRate: 10.6 },
        { id: 'v3', name: 'Use Case Focus', traffic: 34, conversions: 198, conversionRate: 11.7 },
      ],
      startDate: '2023-12-15T00:00:00Z',
      endDate: null,
      targetMetric: 'plan_upgrade_rate',
    },
    {
      id: 'ab3',
      name: 'Credit Bundle Sizes',
      status: 'completed',
      variants: [
        { id: 'v1', name: '1K/5K/10K', traffic: 50, conversions: 890, conversionRate: 22.4 },
        { id: 'v2', name: '2K/8K/20K', traffic: 50, conversions: 1023, conversionRate: 25.8 },
      ],
      startDate: '2023-11-01T00:00:00Z',
      endDate: '2023-12-01T00:00:00Z',
      targetMetric: 'credit_purchase_revenue',
    },
  ],

  priceAdjustments: (): PriceAdjustment[] => [
    { id: 'pa1', tier: 'pro', currentPrice: 49, suggestedPrice: 59, reason: 'Market analysis shows 20% price elasticity room; competitor pricing at $65-79', status: 'pending' },
    { id: 'pa2', tier: 'enterprise', currentPrice: 199, suggestedPrice: 249, reason: 'Feature parity achieved with competitors; value perception survey supports increase', status: 'pending' },
  ],

  churnTriggers: (): ChurnTrigger[] => [
    { id: 'ct1', name: 'Inactivity Warning', condition: 'no_login_days > 7', action: 'send_reengagement_email', enabled: true },
    { id: 'ct2', name: 'Credit Exhaustion', condition: 'credits_remaining < 100 AND tier = "free"', action: 'send_upgrade_offer', enabled: true },
    { id: 'ct3', name: 'Usage Decline', condition: 'weekly_usage_change < -50%', action: 'trigger_cs_outreach', enabled: true },
    { id: 'ct4', name: 'Failed Payments', condition: 'payment_failures > 2', action: 'send_payment_update_request', enabled: true },
    { id: 'ct5', name: 'Feature Drop-off', condition: 'advanced_feature_usage = 0 AND days_since_signup > 14', action: 'send_feature_tutorial', enabled: false },
  ],

  modelRoutingRules: (): ModelRoutingRule[] => [
    { id: 'mr1', name: 'Cost Optimization', condition: 'task_complexity < 0.3', targetModel: 'claude-3-haiku', priority: 1, enabled: true },
    { id: 'mr2', name: 'Quality Priority', condition: 'user.tier = "enterprise"', targetModel: 'claude-3-opus', priority: 2, enabled: true },
    { id: 'mr3', name: 'Balanced Default', condition: 'default', targetModel: 'claude-3-sonnet', priority: 10, enabled: true },
    { id: 'mr4', name: 'Code Tasks', condition: 'task_type = "code_generation"', targetModel: 'claude-3-opus', priority: 1, enabled: true },
    { id: 'mr5', name: 'Simple Queries', condition: 'token_estimate < 500', targetModel: 'claude-3-haiku', priority: 1, enabled: false },
  ],
};
